import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslate } from 'react-polyglot'
import { tokens } from '@getgo/chameleon-core/next/goto-admin'
import { CloseOutlinedIcon } from '@getgo/chameleon-icons/react'
import { IconButton, Typography } from '@getgo/chameleon-web-react-wrapper'
import { Box, Grid, makeStyles } from '@material-ui/core'
import { writeStorage } from '@rehooks/local-storage'
import mixpanel from 'mixpanel-browser'

import { useAccountContext } from 'components/contexts/AccountContext'
import SearchField from 'components/SearchField'
import config from 'config'
import { ManagedAccountsByPartner } from 'hooks/useManagedAccounts'
import { Account } from 'types/Account'
import { mixpanelEventNames } from 'utils'
import usePartnerAccountSettings from '../../hooks/usePartnerAccountSettings'
import MultiplePartners from './MultiplePartners'
import SearchResult from './SearchResult'
import SinglePartner from './SinglePartner'

export const useStyles = makeStyles(() => ({
  root: {
    minHeight: 400,
    flexWrap: 'nowrap',
  },
  searchBar: {
    padding: `${tokens.spacing03} ${tokens.spacing04} ${tokens.spacing03} ${tokens.spacing06}`,
    borderBottom: `1px solid ${tokens.backgroundColorSecondary}`,
  },
  mainMenu: {
    borderRight: `1px solid ${tokens.backgroundColorSecondary}`,
    padding: `${tokens.spacing01} ${tokens.spacing03}`,
    maxHeight: 'calc(100vh - 136px)',
    overflowY: 'auto',
  },
  accountListFilterHolder: {
    padding: `${tokens.spacing01} ${tokens.spacing03}`,
    maxHeight: 'calc(100vh - 136px)',
    overflowY: 'auto',
  },
  accountListHolder: {
    overflowY: 'auto',
    position: 'relative',
  },
  accountList: {
    padding: tokens.spacing03,
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  menuSection: {
    margin: '0.25rem 1rem',
    display: 'flex',
    color: tokens.typeColorSecondary,
    font: tokens.captionMediumStrong,
  },
}))

interface AccountMenuComplexProps {
  closePopover: () => void
  accounts: Account[]
  managedAccountsByPartners: ManagedAccountsByPartner[]
}

const AccountMenuForPartners: FC<AccountMenuComplexProps> = ({
  closePopover,
  accounts,
  managedAccountsByPartners,
  ...props
}) => {
  const [filter, setFilter] = useState<string>('')
  const t = useTranslate()

  const { setSelectedAccountKey, accountKey: currentAccountKey } = useAccountContext()
  const { productSettings } = usePartnerAccountSettings()
  const classes = useStyles()

  const filteredManagedAccountsByPartner: ManagedAccountsByPartner[] = useMemo(() => {
    return managedAccountsByPartners.map(({ partnerAccount, customerAccounts }) => ({
      partnerAccount,
      customerAccounts: customerAccounts.filter(
        (account) => !(productSettings.hideCanceledAccountsFromDropdowns && account.name.startsWith('CANCELED')),
      ),
    }))
  }, [managedAccountsByPartners, productSettings.hideCanceledAccountsFromDropdowns])

  const accountsWithOnlyDirectAccess: Account[] = useMemo(() => {
    const managedAccountKeys: string[] = managedAccountsByPartners.reduce<string[]>(
      (acc, cur) => [...acc, ...cur.customerAccounts.map((account) => account.key)],
      [],
    )
    return accounts.filter((account) => !managedAccountKeys.includes(account.key))
  }, [accounts, managedAccountsByPartners])

  const onSelectAccount = useCallback(
    (account: Account) => {
      if (currentAccountKey !== account.key) {
        setSelectedAccountKey(account.key)
        setFilter('')
        writeStorage(config.SELECTED_ACCOUNT_KEY, { key: account.key })
        mixpanel.track(mixpanelEventNames.ACCOUNT_SWITCHER, {
          Action: `Selected an account`,
          ...account,
        })
      }

      closePopover()
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [closePopover, currentAccountKey],
  )

  return (
    <Grid
      data-testid="account-menu-for-partners"
      container
      direction="column"
      spacing={0}
      alignItems="stretch"
      className={classes.root}
      {...props}
    >
      <Grid item container className={classes.searchBar} alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center">
          <Box mr={6}>
            <Typography variant="heading-small">{t('Accounts')}</Typography>
          </Box>
          <SearchField
            data-testid="account-menu-complex-search-field"
            value={filter}
            onChange={(event) => setFilter(event.target.value || '')}
            onRemove={() => setFilter('')}
          />
        </Box>

        <IconButton data-testid="account-menu-complex-close" size="small" onClick={() => closePopover()}>
          <CloseOutlinedIcon color={tokens.typeColorDefault} />
        </IconButton>
      </Grid>

      <Grid container item xs>
        {filter ? (
          <SearchResult
            filter={filter}
            setFilter={setFilter}
            managedAccountsByPartner={filteredManagedAccountsByPartner}
            onSelectAccount={onSelectAccount}
          />
        ) : filteredManagedAccountsByPartner.length === 1 ? (
          <SinglePartner
            accounts={accountsWithOnlyDirectAccess}
            managedAccountsByPartner={filteredManagedAccountsByPartner[0]}
            onSelectAccount={onSelectAccount}
          />
        ) : (
          <MultiplePartners
            accounts={accountsWithOnlyDirectAccess}
            managedAccountsByPartner={filteredManagedAccountsByPartner}
            onSelectAccount={onSelectAccount}
          />
        )}
      </Grid>
    </Grid>
  )
}

export default AccountMenuForPartners
